// The 5-point zoom control, as the prototype's fixed RIGHT-side vertical slider
// (css/styles.css #zoom-widget): a dark rounded backdrop, a thin track line, five
// dots, and a green thumb that sits on the active point. Click a dot to set the
// canvas scale.
//
// Our zoom is 5 discrete points (ZOOM_SCALES), so this is click-to-set — no
// thumb drag (the prototype's continuous drag doesn't apply to fixed steps).

import { ZOOM_SCALES } from "../store.js";
import { theme } from "../theme.js";

// Vertical centre (px, within the 200px-tall widget) of each dot — the
// prototype's fixed dot tops (6/51/96/141/186) plus the 4px dot half-height.
const DOT_CENTERS = [10, 55, 100, 145, 190];

export default function ZoomBar({ zoomIdx, onZoom }: { zoomIdx: number; onZoom: (idx: number) => void }) {
  return (
    <div
      aria-label="Zoom"
      style={{
        position: "fixed",
        right: 30,
        top: "50%",
        transform: "translateY(-50%)",
        width: 24,
        height: 200,
        zIndex: 50,
      }}
    >
      {/* Dark rounded backdrop (prototype's #zoom-widget::before). */}
      <div
        style={{
          position: "absolute",
          inset: -16,
          background: "rgba(0, 0, 0, 0.75)",
          borderRadius: 12,
          zIndex: -1,
        }}
      />

      {/* Track line. */}
      <div
        style={{
          position: "absolute",
          left: 11,
          top: 10,
          bottom: 10,
          width: 2,
          background: theme.border,
        }}
      />

      {/* One click band per zoom point — a tall transparent hit area so the 8px
          dots are easy to click; the visible dot sits centred inside it. */}
      {ZOOM_SCALES.map((scale, idx) => (
        <button
          key={scale}
          aria-label={`Zoom ${Math.round(scale * 100)}%`}
          aria-pressed={idx === zoomIdx}
          onClick={() => onZoom(idx)}
          style={{
            position: "absolute",
            left: 0,
            width: 24,
            height: 40,
            top: DOT_CENTERS[idx]! - 20,
            padding: 0,
            border: "none",
            background: "transparent",
            cursor: "pointer",
          }}
        >
          <span
            style={{
              position: "absolute",
              left: 8,
              top: "50%",
              transform: "translateY(-50%)",
              width: 8,
              height: 8,
              borderRadius: "50%",
              background: theme.inkDim,
            }}
          />
        </button>
      ))}

      {/* Green thumb — sits over the active dot, animating between points. */}
      <div
        style={{
          position: "absolute",
          left: 4,
          top: DOT_CENTERS[zoomIdx]! - 8,
          width: 16,
          height: 16,
          borderRadius: "50%",
          background: theme.accent,
          boxShadow: "0 2px 6px rgba(0,0,0,0.5)",
          transition: "top 0.15s ease-out",
          pointerEvents: "none",
        }}
      />
    </div>
  );
}
